<template>
  <component
    @click="onClick"
    :is="tag"
    :to="to"
    :class="classes"
    :disabled="tag === 'button' ? loading || disabled : null"
    ref="buttonRef"
  >
    <SpinnerComponent v-if="loading" />
    <IconComponent v-else-if="icon" :name="icon" :key="icon" />
    <slot>{{ title }}</slot>
  </component>
</template>

<script setup>
/**
 * Imports
 */
import { computed, ref } from "vue";
import { SpinnerComponent } from "@/components/ui";

/**
 * Props & Emits
 */
const emit = defineEmits(["click"]);
const props = defineProps({
  variant: {
    type: String,
    default: "white",
    validator: (value) =>
      [
        "main",
        "white",
        "black",
        "black-transparent",
        "gray",
        "gray-light",
        "red",
        "red-transparent",
        "red-outline",
        "white-red",
        "flat",
      ].includes(value),
  },
  size: {
    type: String,
    default: "md",
    validator: (value) => ["sm", "md", "lg"].includes(value),
  },
  title: {
    type: String,
    default: "",
  },
  icon: {
    type: String,
    required: false,
  },
  iconReverse: {
    type: Boolean,
    default: false,
  },
  iconSize: {
    type: String,
    default: "md",
  },
  to: {
    type: Object,
    default: () => null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  tag: {
    type: String,
    default: "button",
  },
});

/**
 * Variables
 */
const buttonRef = ref(null);

/**
 * Computed
 */
const tag = computed(() => (props.to ? "router-link" : props.tag));

// Блокируем повторный клик во время загрузки (защита от двойного сабмита:
// дубли оплат/поступлений/разгрузок) и при явном disabled.
const onClick = (event) => {
  if (props.loading || props.disabled) {
    event?.preventDefault?.();
    return;
  }
  emit("click", event);
};

const classes = computed(() => {
  let classes = `btn btn--${props.size} btn--${props.variant}`;
  if (props.iconReverse) {
    classes += " btn--icon-reverse";
  }
  return classes;
});

const getInstance = () => {
  return buttonRef.value;
};

defineExpose({
  getInstance,
});
</script>

<style lang="stylus"></style>
